/* eslint-disable @typescript-eslint/naming-convention */
/**
 * https://ui.shadcn.com/docs/components/input
 * radix: https://www.radix-ui.com/primitives/docs/components/input
 */
import * as React from 'react';

import { css, cx } from '@pt-group-fe/styled-system/css';
import { input } from '@pt-group-fe/styled-system/recipes';
import { Eye, EyeOff } from 'lucide-react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  readonly?: boolean;
  hasPrefix?: boolean;
  error?: boolean;
  increment?: () => void;
  decrement?: () => void;
}

export interface InputPrefixProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly?: boolean;
  icon?: React.ReactNode;
  error?: boolean;
  iconCl?: string;
}

const InputPrefix = React.forwardRef<HTMLInputElement, InputPrefixProps>((props, ref) => {
  const { className, error, icon, iconCl, children, ...rest } = props;
  const cl = input({ error });

  return (
    <div className={cx(cl.prefix, className)} ref={ref} {...rest}>
      {icon && <span className={cx(cl.icon, iconCl)}>{icon}</span>}
      {children}
    </div>
  );
});
InputPrefix.displayName = 'InputPrefix';

export interface InputAffixProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly?: boolean;
  icon?: React.ReactNode;
  error?: boolean;
  iconCl?: string;
}

const InputAffix = React.forwardRef<HTMLInputElement, InputAffixProps>(
  ({ className, icon, error, children, iconCl, ...rest }, ref) => {
    const cl = input({ error });

    return (
      <div className={cx(cl.affix, className)} ref={ref} {...rest}>
        {icon && <span className={cx(cl.icon, iconCl)}>{icon}</span>}
        {children}
      </div>
    );
  }
);
InputAffix.displayName = 'InputAffix';

export interface InputWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly?: boolean;
  disabled?: boolean;
}

const InputWrapper = React.forwardRef<HTMLDivElement, InputWrapperProps>(
  ({ className, disabled, readonly, ...rest }, ref) => {
    const { wrapper } = input({ disabled, readonly });

    return <div className={cx('group', wrapper, className)} ref={ref} {...rest} />;
  }
);
InputWrapper.displayName = 'InputWrapper';

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ readonly, disabled, type = 'text', className, error, ...props }, ref) => {
    const { root } = input({ readonly, disabled, error });

    return (
      <input
        type={type}
        disabled={disabled}
        readOnly={readonly}
        className={cx(root, className)}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

const InputPassword = React.forwardRef<HTMLInputElement, InputProps>(
  ({ readonly, disabled, error, ...props }, ref) => {
    const [isVisible, setVisible] = React.useState(false);

    return (
      <InputWrapper>
        <Input
          readonly={readonly}
          disabled={disabled}
          error={error}
          {...props}
          type={isVisible ? 'text' : 'password'}
          ref={ref}
        />
        <InputAffix>
          <button
            type="button"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setVisible(!isVisible);
            }}
            className={css({
              cursor: 'pointer',
              opacity: 0.6,
              transition: 'all',
              _hover: { opacity: 2, color: 'primary' }
            })}
          >
            {!isVisible ? <Eye strokeWidth={1.25} /> : <EyeOff strokeWidth={1.25} />}
          </button>
        </InputAffix>
      </InputWrapper>
    );
  }
);
InputPassword.displayName = 'InputPassword';

export { Input, InputAffix, InputPassword, InputPrefix, InputWrapper };
